import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Attach auth token from localStorage on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Domain display names
export const DOMAIN_NAMES = {
  biological_stability: "Biological Brain Stability",
  brain_network_performance: "Brain Network Performance",
  brainwave_regulation: "Brainwave Regulation",
  cognitive_patterns: "Cognitive Pattern Structure",
  belief_architecture: "Belief Architecture",
  behavioral_execution: "Behavioral Execution",
  identity_alignment: "Identity & Purpose Alignment"
};

// Domain short names for charts
export const DOMAIN_SHORT_NAMES = {
  biological_stability: "Biological",
  brain_network_performance: "Network",
  brainwave_regulation: "Brainwave",
  cognitive_patterns: "Cognitive",
  belief_architecture: "Beliefs",
  behavioral_execution: "Behavior",
  identity_alignment: "Identity"
};

// Get score level and color (Updated for MindFlō terminology)
export function getScoreLevel(score) {
  if (score < 25) return { level: 'stable', color: '#10B981', label: 'Balanced' };
  if (score < 50) return { level: 'mild', color: '#F59E0B', label: 'Mild Friction' };
  if (score < 75) return { level: 'moderate', color: '#F97316', label: 'Moderate Friction' };
  return { level: 'high', color: '#EF4444', label: 'High Friction' };
}

// Format date
export function formatDate(dateString) {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}

// RENAMED: Belief Architecture Patterns (from Pattern Clusters)
export const BELIEF_ARCHITECTURE_PATTERNS = {
  performance_pressure_loop: {
    name: "Performance Pressure Loop",
    icon: "Flame",
    color: "#F97316",
    supporting: ["External Validation Dependence", "Failure Avoidance"]
  },
  analysis_paralysis: {
    name: "Analysis Paralysis Pattern",
    icon: "Brain",
    color: "#8B5CF6",
    supporting: ["Over-Control Thinking", "Perfectionism Loop"]
  },
  hidden_belief_saboteur: {
    name: "Hidden Belief Saboteur",
    icon: "Eye",
    color: "#EC4899",
    supporting: ["Unworthiness Pattern", "Self-Sabotage Loop"]
  },
  fragmented_potential: {
    name: "Fragmented High Potential",
    icon: "Sparkles",
    color: "#3B82F6",
    supporting: ["Integration Deficit", "Focus Scatter"]
  },
  identity_drift: {
    name: "Identity Drift System",
    icon: "Compass",
    color: "#6366F1",
    supporting: ["Values Misalignment", "Purpose Void"]
  },
  // Legacy mapping for backward compatibility
  burnout_performer: {
    name: "Performance Pressure Loop",
    icon: "Flame",
    color: "#F97316",
    supporting: ["External Validation Dependence", "Failure Avoidance"]
  },
  overthinking_staller: {
    name: "Analysis Paralysis Pattern",
    icon: "Brain",
    color: "#8B5CF6",
    supporting: ["Over-Control Thinking", "Perfectionism Loop"]
  },
  fragmented_high_potential: {
    name: "Fragmented High Potential",
    icon: "Sparkles",
    color: "#3B82F6",
    supporting: ["Integration Deficit", "Focus Scatter"]
  }
};

// Upgrade Module info
export const UPGRADE_MODULES = {
  belief_reprogramming: {
    name: "Belief Reprogramming",
    icon: "RefreshCw",
    color: "#EC4899"
  },
  cognitive_energy_reset: {
    name: "Cognitive Energy Reset",
    icon: "Zap",
    color: "#F59E0B"
  },
  executive_optimization: {
    name: "Executive Function Optimization",
    icon: "Target",
    color: "#3B82F6"
  },
  neural_state_mastery: {
    name: "Neural State Mastery",
    icon: "Activity",
    color: "#8B5CF6"
  },
  identity_integration: {
    name: "Identity Integration",
    icon: "Compass",
    color: "#6366F1"
  },
  biological_foundation: {
    name: "Biological Foundation Reset",
    icon: "Heart",
    color: "#10B981"
  }
};

// Pathway emoji icons
export const PATHWAY_ICONS = {
  stabilization: '🧘',
  brain_state: '🧠',
  cognitive: '💭',
  execution: '⚡',
  identity: '🎯'
};
